import { useState, useEffect, useCallback, useRef } from 'react';

const API = `${process.env.REACT_APP_API_URL}/api/admin`;

function useAdminFetch(path, token, onUnauthorized) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const onUnauthorizedRef = useRef(onUnauthorized);
  useEffect(() => { onUnauthorizedRef.current = onUnauthorized; }, [onUnauthorized]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}${path}`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      if (res.status === 401) {
        onUnauthorizedRef.current?.();
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || `Request failed (${res.status})`);
      }
      setData(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [path, token]);

  useEffect(() => { load(); }, [load]);

  return { data, loading, error, reload: load };
}

// ── Stat Card ─────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, accent }) {
  return (
    <div style={s.statCard}>
      <div style={{ ...s.statAccent, background: accent || '#F5A623' }} />
      <div style={s.statLabel}>{label}</div>
      <div style={s.statValue}>{value ?? '—'}</div>
      {sub && <div style={s.statSub}>{sub}</div>}
    </div>
  );
}

// ── Status Badge ──────────────────────────────────────────────────────────────
const STATUS_COLOR = {
  healthy:  { bg: '#DCFCE7', text: '#15803D' },
  warning:  { bg: '#FEF9C3', text: '#A16207' },
  error:    { bg: '#FEE2E2', text: '#DC2626' },
  active:   { bg: '#DBEAFE', text: '#1D4ED8' },
  complete: { bg: '#F3F4F6', text: '#6B7280' },
  pending:  { bg: '#FEF9C3', text: '#A16207' },
  paid:     { bg: '#DCFCE7', text: '#15803D' },
};

function Badge({ status }) {
  const c = STATUS_COLOR[status] || STATUS_COLOR.complete;
  return (
    <span style={{ background: c.bg, color: c.text, padding: '2px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 700, textTransform: 'capitalize' }}>
      {status}
    </span>
  );
}

// ── Section wrapper ───────────────────────────────────────────────────────────
function Section({ title, children, action }) {
  return (
    <div style={s.section}>
      <div style={s.sectionHeader}>
        <h2 style={s.sectionTitle}>{title}</h2>
        {action}
      </div>
      {children}
    </div>
  );
}

function LoadBox({ error, onRetry }) {
  if (error) return (
    <div style={s.loadBox}>
      <span style={{ color: '#DC2626' }}>Failed to load — </span>
      <button style={s.retryBtn} onClick={onRetry}>retry</button>
    </div>
  );
  return <div style={s.loadBox}>Loading…</div>;
}

// ── Overview tab ──────────────────────────────────────────────────────────────
function OverviewTab({ token, onUnauthorized }) {
  const { data, loading, error, reload } = useAdminFetch('/stats', token, onUnauthorized);

  if (loading || error) return <LoadBox error={error} onRetry={reload} />;

  return (
    <div>
      <div style={s.statsGrid}>
        <StatCard label="Active Clients"     value={data.activeClients}      sub={`${data.totalClients} total`}              accent="#1A2E44" />
        <StatCard label="Monthly Revenue"    value={`$${data.mrr.toLocaleString()}`} sub="MRR"                              accent="#F5A623" />
        <StatCard label="Total Dashboards"   value={data.totalDashboards}    sub={`${data.dashboardsHealthy} healthy`}       accent="#22C55E" />
        <StatCard label="Needs Attention"    value={data.dashboardsWarning + data.dashboardsError}
                                             sub={`${data.dashboardsWarning} warn · ${data.dashboardsError} err`} accent="#EF4444" />
      </div>

      <div style={s.row}>
        <Section title="Dashboard Health">
          <div style={s.healthBar}>
            {['healthy','warning','error'].map(k => {
              const count = data[`dashboards${k.charAt(0).toUpperCase()+k.slice(1)}`];
              const bg = k === 'healthy' ? '#22C55E' : k === 'warning' ? '#EAB308' : '#EF4444';
              return (
                <div key={k} style={{ ...s.healthSegment, flex: count || 0.01, background: bg }} title={`${k}: ${count}`} />
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: '20px', marginTop: '10px' }}>
            {[['healthy','#22C55E'],['warning','#EAB308'],['error','#EF4444']].map(([k,c]) => (
              <span key={k} style={{ fontSize: '0.82rem', color: '#6B7280', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: c, display: 'inline-block' }} />
                {k.charAt(0).toUpperCase()+k.slice(1)}: <strong style={{ color: '#1A2E44' }}>{data[`dashboards${k.charAt(0).toUpperCase()+k.slice(1)}`]}</strong>
              </span>
            ))}
          </div>
        </Section>

        <Section title="Revenue Split">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '4px' }}>
            {[
              { label: 'Workflows & Dashboards', mrr: 2400, clients: 2 },
              { label: 'Live Dashboards',         mrr: 1300, clients: 2 },
            ].map(r => (
              <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.9rem', color: '#374151' }}>{r.label}</span>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 700, color: '#1A2E44', fontSize: '0.95rem' }}>${r.mrr.toLocaleString()}/mo</div>
                  <div style={{ fontSize: '0.75rem', color: '#9CA3AF' }}>{r.clients} clients</div>
                </div>
              </div>
            ))}
          </div>
        </Section>
      </div>
    </div>
  );
}

// ── Billing tab ───────────────────────────────────────────────────────────────
function BillingTab({ token, onUnauthorized }) {
  const { data, loading, error, reload } = useAdminFetch('/billing', token, onUnauthorized);

  if (loading || error) return <LoadBox error={error} onRetry={reload} />;

  return (
    <div>
      <div style={s.statsGrid}>
        <StatCard label="MRR"            value={`$${data.mrr.toLocaleString()}`}  sub="Monthly recurring"    accent="#1A2E44" />
        <StatCard label="ARR"            value={`$${data.arr.toLocaleString()}`}  sub="Annual run rate"      accent="#F5A623" />
        <StatCard label="Active Clients" value={data.activeClients}               sub="On subscription"      accent="#22C55E" />
      </div>

      <Section title="Recent Invoices">
        <table style={s.table}>
          <thead>
            <tr>
              {['Invoice','Client','Amount','Date','Status'].map(h => (
                <th key={h} style={s.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.invoices.map((inv, i) => (
              <tr key={i} style={i % 2 === 0 ? s.trEven : {}}>
                <td style={s.td}>{inv.id}</td>
                <td style={s.td}>{inv.client}</td>
                <td style={s.td}>${inv.amount.toLocaleString()}</td>
                <td style={s.td}>{inv.date}</td>
                <td style={s.td}><Badge status={inv.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <Section title="Plan Breakdown">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
          {Object.entries(data.byPlan).map(([plan, count]) => (
            <div key={plan} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: '#F8FAFC', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
              <span style={{ fontWeight: 600, color: '#1A2E44', fontSize: '0.93rem' }}>{plan}</span>
              <span style={{ color: '#6B7280', fontSize: '0.88rem' }}>{count} client{count !== 1 ? 's' : ''}</span>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

// ── Dashboards tab ────────────────────────────────────────────────────────────
function DashboardsTab({ token, onUnauthorized }) {
  const { data, loading, error, reload } = useAdminFetch('/dashboards', token, onUnauthorized);

  if (loading || error) return <LoadBox error={error} onRetry={reload} />;

  return (
    <Section title="All Dashboards">
      <table style={s.table}>
        <thead>
          <tr>
            {['Dashboard','Client','Type','Last Refresh','Refresh Rate','Status'].map(h => (
              <th key={h} style={s.th}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.dashboards.map((d, i) => (
            <tr key={d.id} style={i % 2 === 0 ? s.trEven : {}}>
              <td style={{ ...s.td, fontWeight: 600, color: '#1A2E44' }}>{d.name}</td>
              <td style={s.td}>{d.clientName}</td>
              <td style={s.td}>{d.type}</td>
              <td style={s.td}>{new Date(d.lastRefresh).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
              <td style={s.td}>{d.refreshRate}</td>
              <td style={s.td}><Badge status={d.status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </Section>
  );
}

// ── Clients tab ───────────────────────────────────────────────────────────────
function ClientsTab({ token, onUnauthorized }) {
  const { data, loading, error, reload } = useAdminFetch('/clients', token, onUnauthorized);

  if (loading || error) return <LoadBox error={error} onRetry={reload} />;

  return (
    <Section title="All Clients">
      <table style={s.table}>
        <thead>
          <tr>
            {['Client','Email','Plan','MRR','Since','Status'].map(h => (
              <th key={h} style={s.th}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.clients.map((c, i) => (
            <tr key={c.id} style={i % 2 === 0 ? s.trEven : {}}>
              <td style={{ ...s.td, fontWeight: 600, color: '#1A2E44' }}>{c.name}</td>
              <td style={s.td}>{c.email}</td>
              <td style={s.td}>{c.plan}</td>
              <td style={s.td}>{c.mrr ? `$${c.mrr}/mo` : '—'}</td>
              <td style={s.td}>{c.since}</td>
              <td style={s.td}><Badge status={c.status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </Section>
  );
}

// ── Main AdminPortal ──────────────────────────────────────────────────────────
const TABS = ['Overview', 'Billing', 'Dashboards', 'Clients'];

export default function AdminPortal({ user, token, onExit, onUnauthorized }) {
  const [tab, setTab] = useState('Overview');

  return (
    <div style={s.shell}>
      {/* Top bar */}
      <div style={s.topBar}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <img src="/yhd_logo.png" alt="Yellowhammer Data" style={s.logo} />
          <div style={s.adminBadge}>Admin</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <span style={s.userLabel}>{user?.email}</span>
          <button style={s.exitBtn} onClick={onExit}>Exit Admin</button>
        </div>
      </div>

      {/* Tabs */}
      <div style={s.tabBar}>
        {TABS.map(t => (
          <button
            key={t}
            style={{ ...s.tabBtn, ...(tab === t ? s.tabBtnActive : {}) }}
            onClick={() => setTab(t)}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={s.content}>
        {tab === 'Overview'    && <OverviewTab   token={token} onUnauthorized={onUnauthorized} />}
        {tab === 'Billing'     && <BillingTab    token={token} onUnauthorized={onUnauthorized} />}
        {tab === 'Dashboards'  && <DashboardsTab token={token} onUnauthorized={onUnauthorized} />}
        {tab === 'Clients'     && <ClientsTab    token={token} onUnauthorized={onUnauthorized} />}
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const s = {
  shell:        { minHeight: '100vh', background: '#F8FAFC', fontFamily: 'inherit' },
  topBar:       { background: '#1A2E44', padding: '0 32px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  logo:         { height: '40px', filter: 'brightness(0) invert(1)' },
  adminBadge:   { background: '#F5A623', color: '#1A2E44', fontSize: '0.7rem', fontWeight: 800, padding: '3px 10px', borderRadius: '20px', letterSpacing: '0.06em', textTransform: 'uppercase' },
  userLabel:    { color: 'rgba(255,255,255,0.65)', fontSize: '0.88rem' },
  exitBtn:      { background: 'transparent', color: 'rgba(255,255,255,0.7)', border: '1.5px solid rgba(255,255,255,0.25)', borderRadius: '8px', padding: '7px 16px', fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'inherit' },

  tabBar:       { background: 'white', borderBottom: '1px solid #E5E7EB', padding: '0 32px', display: 'flex', gap: '4px' },
  tabBtn:       { background: 'none', border: 'none', borderBottom: '2px solid transparent', padding: '14px 18px', fontSize: '0.9rem', fontWeight: 600, color: '#6B7280', cursor: 'pointer', fontFamily: 'inherit', marginBottom: '-1px' },
  tabBtnActive: { color: '#1A2E44', borderBottom: '2px solid #F5A623' },

  content:      { maxWidth: '1100px', margin: '0 auto', padding: '32px 24px' },

  statsGrid:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' },
  statCard:     { background: 'white', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '20px 20px 16px', position: 'relative', overflow: 'hidden' },
  statAccent:   { position: 'absolute', top: 0, left: 0, right: 0, height: '4px' },
  statLabel:    { fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#9CA3AF', marginBottom: '8px', marginTop: '4px' },
  statValue:    { fontSize: '1.8rem', fontWeight: 800, color: '#1A2E44', lineHeight: 1 },
  statSub:      { fontSize: '0.78rem', color: '#9CA3AF', marginTop: '6px' },

  row:          { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' },

  section:      { background: 'white', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '20px 24px', marginBottom: '20px' },
  sectionHeader:{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' },
  sectionTitle: { fontSize: '1rem', fontWeight: 700, color: '#1A2E44', margin: 0 },

  healthBar:    { display: 'flex', borderRadius: '6px', overflow: 'hidden', height: '12px', gap: '2px' },
  healthSegment:{ transition: 'flex 0.3s' },

  table:        { width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' },
  th:           { textAlign: 'left', padding: '10px 12px', color: '#6B7280', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #E5E7EB' },
  td:           { padding: '12px 12px', color: '#374151', borderBottom: '1px solid #F3F4F6' },
  trEven:       { background: '#FAFAFA' },

  loadBox:      { padding: '40px', textAlign: 'center', color: '#9CA3AF', fontSize: '0.93rem' },
  retryBtn:     { background: 'none', border: 'none', color: '#1A2E44', textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.93rem' },
};
